import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert, AlertTitle, Box, Button, CircularProgress, Drawer, Fab, IconButton, Paper, Stack, TextField, Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import brand from '../shell/brand';
import AssistantMark from '../shell/AssistantMark';
import { request } from '../operations/OperationsProvider';
import { formatDateTime } from '../common/util/datetime';

// Ask the assistant something, without leaving what you were looking at.
//
// A question about the plant is nearly always asked while looking at something else - the map, a
// trip, yesterday's report - and a page would make somebody leave the thing that prompted the
// question. So it opens over the page and closes again, and the page underneath is undisturbed.
//
// What it will not do is pretend. Every figure comes from the same reads the screens use, called as
// the person asking, so a question can only return what that person could already open; and a
// question it does not recognise is answered with "I cannot answer that one" and a list of what it
// can, never with a guess. These answers get repeated in meetings, so the same question has to give
// the same answer.

const AssistantWindow = () => {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState(null);
  const [failedQuestion, setFailedQuestion] = useState('');
  const active = useRef(null);
  const pendingQuestion = useRef('');
  // Whose conversation this is. Two people signing in to the same browser - a shared desk at the
  // gate is the ordinary case, not the unusual one - must not inherit each other's questions, so
  // the account is part of the key, as it is for saved drafts elsewhere in this product.
  const account = useSelector((state) => state.session.user);
  const key = `assistant.${account?.tenantId}.${account?.id}`;

  // Oldest first, the way a conversation reads. Kept while the window is closed so somebody who
  // opens it again has not lost the figure they were about to quote, and kept across a reload for
  // the same reason - in this browser only, never on the server, because a question somebody typed
  // is theirs and there is no reason for it to outlive their session or leave their machine.
  const [said, setSaid] = useState(() => {
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(key) || '[]');
      return Array.isArray(saved) ? saved.filter((entry) => typeof entry?.question === 'string' && typeof entry?.headline === 'string').slice(-20) : [];
    } catch { return []; }
  });
  useEffect(() => {
    // Storage can be unavailable or full, and a conversation is not worth failing a page over.
    try { window.sessionStorage.setItem(key, JSON.stringify(said.slice(-20))); } catch { /* unavailable storage */ }
  }, [key, said]);
  const clear = () => {
    active.current?.abort(); active.current = null;
    setAsking(false); setQuestion(''); setFailedQuestion('');
    setSaid([]);
    setError(null);
    try { window.sessionStorage.removeItem(key); } catch { /* unavailable storage */ }
  };
  const foot = useRef(null);
  useEffect(() => () => { active.current?.abort(); active.current = null; }, []);

  useEffect(() => {
    if (open) foot.current?.scrollIntoView({ block: 'end' });
  }, [open, said.length, asking]);

  const ask = async (text) => {
    const wanted = (text ?? question).trim();
    if (!wanted || active.current) return;
    const controller = new AbortController(); active.current = controller;
    pendingQuestion.current = wanted;
    const timer = setTimeout(() => controller.abort('timeout'), 45000);
    controller.signal.addEventListener('abort', () => clearTimeout(timer), { once: true });
    setAsking(true);
    setError(null);
    setQuestion('');
    try {
      // What the last answer left behind, so "why was it late" knows which lorry. Carried by the
      // window rather than remembered by the server: two people asking at once cannot then inherit
      // each other's subject, and nothing has to be kept between requests.
      const answer = await request('/api/operations/ask', 'POST', {
        question: wanted,
        carry: said[said.length - 1]?.carry ?? null,
        // What has already been said, so a follow-up has something to refer to. Only the words on
        // screen go back - the question and the headline answered to it - never anything the
        // assistant looked up to produce them.
        history: said.slice(-4).map((entry) => ({ question: entry.question, answer: entry.headline })),
      }, controller.signal);
      if (active.current === controller) {
        setSaid((before) => [...before, answer].slice(-20)); setFailedQuestion('');
      }
    } catch (e) {
      if (active.current === controller) {
        setError(controller.signal.aborted ? 'The request timed out. You can retry your question.' : e.message);
        setFailedQuestion(wanted); setQuestion((draft) => draft || wanted);
      }
    } finally {
      clearTimeout(timer);
      if (active.current === controller) { active.current = null; setAsking(false); }
    }
  };
  const cancel = () => {
    active.current?.abort(); active.current = null; setAsking(false);
    setQuestion((draft) => draft || pendingQuestion.current);
  };

  return (
    <>
      {/* Clear of the bottom menu on a phone, where that strip is the one thing that must never be
          covered - it is how everything else is reached. */}
      <Tooltip title={`Ask ${brand.assistant}`}>
        <Fab
          color="default"
          size="medium"
          aria-label={`Ask ${brand.assistant}`}
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            right: 16,
            zIndex: (theme) => theme.zIndex.drawer - 1,
            bottom: { xs: 72, md: 24 },
            // The badge is the button. It carries its own ground, so sitting it on a coloured one
            // puts two grounds on top of each other and reads as neither.
            p: 0,
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          <AssistantMark size={48} />
        </Fab>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        // Above the bottom navigation, which this application fixes at 1204 - a drawer left at the
        // theme's own 1200 opens underneath it on a phone, and the bar covers the very row the
        // person types into.
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
        // Ready to be typed into the moment it is open. Somebody opens this to ask something, and
        // a box they have to click first is a step between them and the question. Focused when the
        // panel has finished sliding rather than as it mounts, so it is not taken straight back by
        // the drawer settling its own focus.
        SlideProps={{ onEntered: () => box.current?.focus() }}
        PaperProps={{
          sx: {
            width: { xs: '100%', md: 420 },
            maxWidth: '100vw',
            // A column, so the conversation scrolls and the composer stays on the bottom edge.
            // Without this the list cannot size itself and the composer is pushed off a phone
            // screen entirely - which is short enough to hide on a desktop panel and not on a
            // full-height one.
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Stack direction="row" gap={1.5} alignItems="center">
            <AssistantMark size={28} />
            <Box>
              <Typography variant="h6">{brand.assistant}</Typography>
              {/* The whole invitation, in four words. What stood here described the software to
                  somebody who had come to ask a question, and the panel said it again underneath
                  in longer form. Nothing is offered to press: a question somebody was handed is
                  not the question they had, and a box that proposes its own questions teaches
                  people to ask only those - which is how the one thing it cannot answer stops
                  being asked, and stops being noticed. */}
              <Typography variant="caption" color="text.secondary">
                How can I help?
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" gap={0.5}>
            {/* Two different endings, and the words have to tell them apart. Closing the
                conversation finishes it: the questions go, and the box is ready for new ones.
                Hiding the panel puts it down - somebody who has read a figure and wants their
                screen back has not finished asking. */}
            {said.length > 0 && (
              <Tooltip title="Close conversation">
                <IconButton aria-label="Close conversation" onClick={clear}><DeleteSweepIcon /></IconButton>
              </Tooltip>
            )}
            <Tooltip title={`Hide ${brand.assistant}`}>
              <IconButton aria-label={`Hide ${brand.assistant}`} onClick={() => setOpen(false)}><CloseIcon /></IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
          <Stack gap={2}>
            {said.map((answer, index) => (
              // Keyed on the question and its place in the conversation: the same question asked
              // twice is two entries, and must not collapse into one.
              // eslint-disable-next-line react/no-array-index-key
              <Box key={`${index}-${answer.question}`}>
                <Typography
                  variant="body2"
                  sx={{
                    ml: 'auto',
                    maxWidth: '85%',
                    width: 'fit-content',
                    px: 1.5,
                    py: 1,
                    mb: 1,
                    bgcolor: 'action.selected',
                    borderRadius: 2,
                  }}
                >
                  {answer.question}
                </Typography>
                <Stack direction="row" gap={1.25} alignItems="flex-start">
                  <Box sx={{ mt: 0.5 }}><AssistantMark size={24} /></Box>
                  <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" fontWeight={600}>{answer.headline}</Typography>
                    {answer.choices?.length > 0 && (
                      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                        {answer.choices.map((choice) => <Button size="small" variant="outlined" key={choice} disabled={asking} onClick={() => ask(choice)}>{choice}</Button>)}
                      </Stack>
                    )}
                    <Stack gap={0.5} sx={{ mt: (answer.detail || []).length ? 1 : 0 }}>
                      {(answer.detail || []).map((line) => (
                        <Typography key={line} variant="body2" color="text.secondary">{line}</Typography>
                      ))}
                    </Stack>
                    {/* A file the assistant prepared. The link comes from the tool that built it
                        rather than from the answer's wording, so there is nothing for a model to
                        mistype, and the rows are read when the person asks for them. */}
                    {answer.download && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        href={answer.download}
                        sx={{ mt: 1.5 }}
                      >
                        {answer.downloadName || 'Download'}
                      </Button>
                    )}
                    {/* Most of these are worked out on the scheduler, so "now" would be a lie about
                        several of them. The answer carries when it was computed; the window says it. */}
                    {answer.asOf && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        {`As at ${formatDateTime(answer.asOf)}`}
                      </Typography>
                    )}
                    {answer.source && <Typography variant="caption" color="text.secondary" display="block">{`Source: ${answer.source}`}</Typography>}
                  </Paper>
                </Stack>
              </Box>
            ))}

            {asking && (
              <Stack direction="row" gap={1.25} alignItems="center">
                <AssistantMark size={24} />
                <CircularProgress size={16} />
                <Button size="small" onClick={cancel}>Cancel</Button>
              </Stack>
            )}

            {/* Only a failure to reach the server is an error here. A question it cannot answer is
                not one - that comes back as an answer which says so. */}
            {error && (
              <Alert
                severity="warning"
                action={failedQuestion && <Button color="inherit" size="small" disabled={asking} onClick={() => ask(failedQuestion)}>Retry</Button>}
                sx={{ alignItems: 'flex-start', '& .MuiAlert-message': { minWidth: 0, overflowWrap: 'anywhere' } }}
              >
                <AlertTitle sx={{ mb: 0.25, fontWeight: 700 }}>That question did not reach us</AlertTitle>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{error}</Typography>
              </Alert>
            )}

            <div ref={foot} />
          </Stack>
        </Box>

        <Stack
          direction="row"
          gap={1}
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            flexShrink: 0,
            // Clear of the home indicator on a phone that has one, where the last row of a
            // full-height panel otherwise sits under it.
            pb: 'calc(16px + env(safe-area-inset-bottom))',
          }}
        >
          <TextField
            id="assistant-question"
            inputRef={box}
            fullWidth
            size="small"
            autoComplete="off"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') ask(); }}
            placeholder={`Ask ${brand.assistant}…`}
            inputProps={{ maxLength: 400, 'aria-label': 'Your question about the plant' }}
          />
          <IconButton color="primary" aria-label="Ask" onClick={() => ask()} disabled={asking || !question.trim()}>
            <SendIcon />
          </IconButton>
        </Stack>
      </Drawer>
    </>
  );
};

export default AssistantWindow;
